import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MsalService } from '@azure/msal-angular';
import { InternalNotification, NotificationStorage } from './notification';

@Injectable()
export class NotificationService {
	private readonly snackBar = inject(MatSnackBar);
	private readonly authService = inject(MsalService);
	private readonly userId = signal<string>('');
	private readonly storagePrefix = 'lvr_notifications_';
	private readonly storageKey = computed(() => `${this.storagePrefix}${this.userId()}`);
	private readonly storage = signal<NotificationStorage | undefined>(undefined);

	public constructor() {
		const accounts = this.authService.instance.getAllAccounts();
		if (accounts && accounts.length > 0) {
			this.userId.set(accounts[0].localAccountId);
		} else {
			console.warn('No active user account found. Notifications may not be personalized.');
		}

		const storageData = this.getUserNotificationStorage();
		this.storage.set(storageData);
		effect(() => {
			const data = this.storage();
			this.saveNotificationStorage(data!);
		});
	}

	public addNotification(notification: InternalNotification) {
		const currentStorage = this.storage();
		if (!currentStorage) {
			console.warn('Notification storage is not initialized.');
			return;
		}
		const updatedStorage: NotificationStorage = {
			...currentStorage,
			notifications: [...currentStorage.notifications, notification],
			lastUpdated: Date.now(),
		};

		if (notification.showToast) {
			this.snackBar.open(notification.message, 'OK', {
				duration: 3000,
			});
		}

		this.storage.set(updatedStorage);
	}

	private getUserNotificationStorage() {
		const userId = this.userId();
		if (!userId) {
			console.warn('User ID is not set. Cannot access notification storage.');
			return;
		}

		const item = localStorage.getItem(this.storageKey());
		if (item) {
			return JSON.parse(item) as NotificationStorage;
		}
		console.warn('No notification storage found for user:', userId);
		const newStorage: NotificationStorage = {
			lastUpdated: new Date().getTime(),
			notifications: [],
			storageKey: this.storageKey(),
			userId: userId,
		};
		return newStorage;
	}

	private saveNotificationStorage(storage: NotificationStorage) {
		localStorage.setItem(this.storageKey(), JSON.stringify(storage));
	}
}
